import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage, RegisterPage, VerifyEmailPage } from '@/pages/auth'
import { DashboardPage } from '@/pages/dashboard'
import MarketplacePage from '@/pages/marketplace/MarketplacePage'
import ListingDetailPage from '@/pages/marketplace/ListingDetailPage'
import ProfilePage from '@/pages/profile/ProfilePage'
import FavoritesPage from '@/pages/favorites/FavoritesPage'
import CheckoutRedirectPage from '@/pages/checkout/CheckoutRedirectPage'
import CheckoutSuccessPage from '@/pages/checkout/CheckoutSuccessPage'
import AdminLoginPage from '@/pages/admin/AdminLoginPage'
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage'
import AnalyticsPage from '@/pages/admin/AnalyticsPage'
import BulkImportPage from '@/pages/admin/BulkImportPage'
import AdminFulfillmentPage from '@/pages/admin/AdminFulfillmentPage'
import PaymentTestPage from '@/pages/admin/PaymentTestPage'
import UsbNfcWriterPage from '@/pages/admin/UsbNfcWriterPage'
import VerifyPage from '@/pages/scan/VerifyPage'
import HowItWorksPage from '@/pages/HowItWorksPage'
import { useAppConfig } from '@/hooks/useAppConfig'
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage'
import ReturnPolicyPage from '@/pages/ReturnPolicyPage'
import TermsOfServicePage from '@/pages/TermsOfServicePage'
import SmsConsentPage from '@/pages/SmsConsentPage'
import { ProtectedRoute, AdminRoute } from '@/components/auth'
import { MainLayout } from '@/components/layout'

// The USB NFC writer is meaningless in mock mode (no hardware) — redirect away so NFC never
// surfaces. The route stays registered so flipping mock mode off restores it instantly.
function NfcWriterRoute() {
  const { config, loading } = useAppConfig()
  if (loading) return null
  if (config.nfcMockMode) return <Navigate to="/admin/dashboard" replace />
  return <UsbNfcWriterPage />
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route
          path="/favorites"
          element={
            <MainLayout>
              <FavoritesPage />
            </MainLayout>
          }
        />
        
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route 
          path="/admin/dashboard" 
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <AdminRoute>
              <AnalyticsPage />
            </AdminRoute>
          } 
        />
        <Route
          path="/admin/bulk-import"
          element={
            <AdminRoute>
              <BulkImportPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/fulfillment"
          element={
            <AdminRoute>
              <AdminFulfillmentPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/payment-test"
          element={
            <AdminRoute>
              <PaymentTestPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/nfc-writer"
          element={
            <AdminRoute>
              <NfcWriterRoute />
            </AdminRoute>
          }
        />

        {/* Public marketplace routes */}
        <Route
          path="/marketplace"
          element={
            <MainLayout>
              <MarketplacePage />
            </MainLayout>
          }
        />
        <Route
          path="/marketplace/:slug"
          element={
            <MainLayout>
              <ListingDetailPage />
            </MainLayout>
          }
        />

        {/* Public info pages */}
        <Route
          path="/how-it-works"
          element={
            <MainLayout>
              <HowItWorksPage />
            </MainLayout>
          }
        />
        <Route
          path="/privacy"
          element={
            <MainLayout>
              <PrivacyPolicyPage />
            </MainLayout>
          }
        />
        <Route
          path="/return-policy"
          element={
            <MainLayout>
              <ReturnPolicyPage />
            </MainLayout>
          }
        />
        <Route
          path="/terms"
          element={
            <MainLayout>
              <TermsOfServicePage />
            </MainLayout>
          }
        />
        <Route
          path="/sms-consent"
          element={
            <MainLayout>
              <SmsConsentPage />
            </MainLayout>
          }
        />
        {/* Demo routes removed in favor of live listing slugs */}
        
        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Checkout routes */}
        <Route
          path="/checkout/:orderId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CheckoutRedirectPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/:orderId/success"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CheckoutSuccessPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/marketplace" replace />} />
        <Route path="*" element={<Navigate to="/marketplace" replace />} />
      </Routes>
    </Router>
  )
}

export default App
